import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { employeeService } from '../services/employeeService';
import { certificationService } from '../services/certificationService';
import { applicantService } from '../services/applicantService';
import { toast } from 'react-toastify';
import api from '../services/api';
import RosterUpload from '../components/RosterUpload';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionContext';

// ============================================
// STYLED COMPONENTS
// ============================================

const styles = {
  pageContainer: { padding: '0', background: '#141414', minHeight: '100vh' },
  header: { background: 'linear-gradient(135deg, #000000 0%, #1a1a1a 100%)', padding: '32px 40px', marginBottom: '0' },
  headerContent: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', maxWidth: '1400px', margin: '0 auto' },
  headerTitle: { color: 'white', fontSize: '28px', fontWeight: '700', margin: 0, letterSpacing: '-0.5px' },
  headerSubtitle: { color: 'rgba(255,255,255,0.7)', fontSize: '14px', marginTop: '4px' },
  headerActions: { display: 'flex', gap: '12px' },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0', background: '#1E1E1E', borderBottom: '1px solid #2A2A2A', boxShadow: '0 1px 3px rgba(0,0,0,0.3), 0 0 25px rgba(212, 169, 78, 0.35), 0 0 50px rgba(212, 169, 78, 0.15), 0 0 80px rgba(212, 169, 78, 0.05)' },
  statCard: { padding: '24px 32px', borderRight: '1px solid #2A2A2A', display: 'flex', alignItems: 'center', gap: '16px', boxShadow: '0 0 25px rgba(212, 169, 78, 0.35), 0 0 50px rgba(212, 169, 78, 0.15), 0 0 80px rgba(212, 169, 78, 0.05)' },
  statIcon: { width: '48px', height: '48px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' },
  statContent: { flex: 1 },
  statValue: { fontSize: '28px', fontWeight: '700', color: '#FFFFFF', lineHeight: 1 },
  statLabel: { fontSize: '13px', color: '#A0A0A0', marginTop: '4px', fontWeight: '500' },
  mainContent: { maxWidth: '1400px', margin: '0 auto', padding: '24px 40px' },
  tabsContainer: { display: 'flex', gap: '4px', background: '#1A1A1A', padding: '4px', borderRadius: '12px', marginBottom: '24px', width: 'fit-content' },
  tab: { padding: '10px 20px', border: 'none', background: 'transparent', color: '#A0A0A0', fontSize: '14px', fontWeight: '500', cursor: 'pointer', borderRadius: '8px', transition: 'all 0.2s ease' },
  tabActive: { background: '#2A2A2A', color: '#FFFFFF', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' },
  card: { background: '#1E1E1E', borderRadius: '16px', boxShadow: '0 0 15px rgba(212, 169, 78, 0.12), 0 0 30px rgba(212, 169, 78, 0.06)', overflow: 'hidden' },
  cardHeader: { padding: '20px 24px', borderBottom: '1px solid #2A2A2A', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  cardTitle: { fontSize: '16px', fontWeight: '600', color: '#FFFFFF', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' },
  cardBody: { padding: '24px' },
  btnPrimary: { background: 'linear-gradient(135deg, #D4A94E 0%, #C49A3E 100%)', color: '#1A1A1A', border: 'none', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s ease' },
  btnSecondary: { background: '#1E1E1E', color: '#E0E0E0', border: '1px solid #2A2A2A', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s ease' },
  btnSmall: { padding: '6px 12px', fontSize: '13px' },
  gridTwo: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' },
  gridThree: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' },
  table: { width: '100%', borderCollapse: 'collapse' },
  tableHeader: { background: '#1A1A1A' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#A0A0A0', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #2A2A2A' },
  td: { padding: '16px', borderBottom: '1px solid #2A2A2A', fontSize: '14px', color: '#E0E0E0' },
  badge: { padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '4px' },
  emptyState: { padding: '60px 40px', textAlign: 'center', color: '#A0A0A0' },
  modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalContent: { background: '#1E1E1E', borderRadius: '16px', padding: '32px', width: '500px', maxWidth: '90%', maxHeight: '90vh', overflow: 'auto' },
};

// ============================================
// COMPONENTS
// ============================================

const DonutChart = ({ percentage, size = 140, strokeWidth = 14 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;
  const color = percentage >= 80 ? '#10b981' : percentage >= 50 ? '#D4A94E' : '#ef4444';
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="#2A2A2A" strokeWidth={strokeWidth} />
        <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={color} strokeWidth={strokeWidth} strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
      </svg>
      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
        <div style={{ fontSize: '32px', fontWeight: '700', color }}>{percentage}%</div>
        <div style={{ fontSize: '11px', color: '#6B6B6B', fontWeight: '500' }}>COMPLIANT</div>
      </div>
    </div>
  );
};

const ProgressBar = ({ label, percentage, completed, total }) => {
  const color = percentage >= 80 ? '#10b981' : percentage >= 50 ? '#D4A94E' : '#ef4444';
  return (
    <div style={{ marginBottom: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
        <span style={{ fontSize: '14px', fontWeight: '500', color: '#E0E0E0' }}>{label}</span>
        <span style={{ fontSize: '14px', fontWeight: '600', color }}>{percentage}%</span>
      </div>
      <div style={{ height: '8px', background: '#2A2A2A', borderRadius: '4px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${percentage}%`, background: color, borderRadius: '4px', transition: 'width 0.5s ease' }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px', fontSize: '12px', color: '#6B6B6B' }}>
        <span>{completed} signed</span>
        <span>{total - completed} remaining</span>
      </div>
    </div>
  );
};

// ============================================
// APPLE-STYLE EMPLOYEE BROWSER COMPONENT
// ============================================

const EmployeeBrowser = ({ onViewEmployee }) => {
  const [employees, setEmployees] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [letters, setLetters] = useState([]);
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await api.get('/compliance/employees');
      const empList = response.data || [];
      setEmployees(empList);
      
      // Group by first letter of last name
      const groups = {};
      empList.forEach(emp => {
        const letter = emp.lastName.charAt(0).toUpperCase();
        if (!groups[letter]) groups[letter] = [];
        groups[letter].push(emp);
      });
      setGrouped(groups);
      setLetters(Object.keys(groups).sort());
    } catch (err) {
      console.error('Failed to load employees:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredEmployees = () => {
    let result = employees;
    
    // Filter by status
    if (statusFilter === 'completed') {
      result = result.filter(e => e.isComplete);
    } else if (statusFilter === 'pending') {
      result = result.filter(e => !e.isComplete);
    }
    
    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(e => 
        e.firstName.toLowerCase().includes(query) || 
        e.lastName.toLowerCase().includes(query)
      );
    }
    
    // Filter by letter
    if (selectedLetter) {
      result = result.filter(e => e.lastName.charAt(0).toUpperCase() === selectedLetter);
    }
    
    return result;
  };

  const getStatusBadge = (emp) => {
    if (emp.isComplete) {
      return { bg: 'rgba(34,197,94,0.1)', color: '#4ADE80', text: '✓ Complete', icon: '✅' };
    } else if (emp.overallStatus === 'pending_hr') {
      return { bg: 'rgba(212,169,78,0.1)', color: '#D4A94E', text: '⏳ Pending HR', icon: '⏳' };
    } else if (emp.overallStatus === 'in_progress') {
      return { bg: 'rgba(37,99,235,0.1)', color: '#60A5FA', text: '📝 In Progress', icon: '📝' };
    }
    return { bg: '#2A2A2A', color: '#A0A0A0', text: 'Not Started', icon: '○' };
  };

  const scrollToLetter = (letter) => {
    setSelectedLetter(letter === selectedLetter ? null : letter);
    const element = document.getElementById(`letter-${letter}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const displayed = filteredEmployees();
  const completedCount = employees.filter(e => e.isComplete).length;
  const pendingCount = employees.filter(e => !e.isComplete).length;

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#6B6B6B' }}>
        Loading employees...
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: '0', height: '500px', border: '1px solid #2A2A2A', borderRadius: '12px', overflow: 'hidden' }}>
      {/* Left side - A-Z Navigator */}
      <div style={{ width: '40px', background: '#1A1A1A', borderRight: '1px solid #2A2A2A', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '8px 0', overflowY: 'auto' }}>
        {letters.map(letter => (
          <button
            key={letter}
            onClick={() => scrollToLetter(letter)}
            style={{
              width: '28px',
              height: '28px',
              border: 'none',
              background: selectedLetter === letter ? '#D4A94E' : 'transparent',
              color: selectedLetter === letter ? '#1A1A1A' : '#A0A0A0',
              fontSize: '12px',
              fontWeight: '600',
              borderRadius: '6px',
              cursor: 'pointer',
              marginBottom: '2px',
              transition: 'all 0.15s ease'
            }}
          >
            {letter}
          </button>
        ))}
      </div>

      {/* Main content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Search and filters */}
        <div style={{ padding: '16px', borderBottom: '1px solid #2A2A2A', background: '#1A1A1A' }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <input
                type="text"
                placeholder="Search employees..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px 10px 36px',
                  border: '1px solid #2A2A2A',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  background: '#141414',
                  color: '#FFFFFF'
                }}
              />
              <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6B6B6B' }}>🔍</span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setStatusFilter('all')}
              style={{
                padding: '6px 12px',
                border: 'none',
                borderRadius: '16px',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
                background: statusFilter === 'all' ? '#FFFFFF' : '#2A2A2A',
                color: statusFilter === 'all' ? '#1A1A1A' : '#A0A0A0'
              }}
            >
              All ({employees.length})
            </button>
            <button
              onClick={() => setStatusFilter('completed')}
              style={{
                padding: '6px 12px',
                border: 'none',
                borderRadius: '16px',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
                background: statusFilter === 'completed' ? 'rgba(16,185,129,0.15)' : '#2A2A2A',
                color: statusFilter === 'completed' ? '#4ADE80' : '#A0A0A0'
              }}
            >
              ✓ Completed ({completedCount})
            </button>
            <button
              onClick={() => setStatusFilter('pending')}
              style={{
                padding: '6px 12px',
                border: 'none',
                borderRadius: '16px',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
                background: statusFilter === 'pending' ? '#D4A94E' : '#2A2A2A',
                color: statusFilter === 'pending' ? '#1A1A1A' : '#A0A0A0'
              }}
            >
              ⏳ Pending ({pendingCount})
            </button>
            {selectedLetter && (
              <button
                onClick={() => setSelectedLetter(null)}
                style={{
                  padding: '6px 12px',
                  border: '1px solid #2A2A2A',
                  borderRadius: '16px',
                  fontSize: '12px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  background: '#1E1E1E',
                  color: '#A0A0A0'
                }}
              >
                Clear "{selectedLetter}" filter ×
              </button>
            )}
          </div>
        </div>

        {/* Employee list */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {displayed.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6B6B6B' }}>
              <span style={{ fontSize: '32px' }}>🔍</span>
              <p>No employees found</p>
            </div>
          ) : (
            Object.keys(grouped).sort().map(letter => {
              const letterEmployees = displayed.filter(e => e.lastName.charAt(0).toUpperCase() === letter);
              if (letterEmployees.length === 0) return null;
              
              return (
                <div key={letter} id={`letter-${letter}`}>
                  <div style={{
                    padding: '8px 16px',
                    background: '#1A1A1A',
                    fontWeight: '600',
                    fontSize: '13px',
                    color: '#A0A0A0',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1
                  }}>
                    {letter}
                  </div>
                  {letterEmployees.map(emp => {
                    const status = getStatusBadge(emp);
                    return (
                      <div
                        key={emp.id}
                        onClick={() => onViewEmployee && onViewEmployee(emp)}
                        style={{
                          padding: '12px 16px',
                          borderBottom: '1px solid #2A2A2A',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          cursor: 'pointer',
                          transition: 'background 0.15s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#252525'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          background: emp.isComplete ? 'rgba(34,197,94,0.1)' : 'rgba(212,169,78,0.1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: emp.isComplete ? '#4ADE80' : '#D4A94E'
                        }}>
                          {emp.firstName.charAt(0)}{emp.lastName.charAt(0)}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '500', color: '#FFFFFF' }}>
                            {emp.lastName}, {emp.firstName}
                          </div>
                          <div style={{ fontSize: '12px', color: '#6B6B6B' }}>
                            {emp.forms?.map(f => (
                              <span key={f.formId} style={{ marginRight: '8px' }}>
                                {f.status === 'completed' ? '✅' : f.status === 'employee_signed' ? '⏳' : '○'} {f.formName?.replace('Employee ', '').replace(' Agreement', '')}
                              </span>
                            ))}
                          </div>
                        </div>
                        <span style={{
                          padding: '4px 10px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: '600',
                          background: status.bg,
                          color: status.color
                        }}>
                          {status.text}
                        </span>
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

const HumanResources = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [stats, setStats] = useState(null);
  const [expiringCerts, setExpiringCerts] = useState([]);
  const [applications, setApplications] = useState([]);
  const [applicationStats, setApplicationStats] = useState({});
  const [selectedApplication, setSelectedApplication] = useState(null);
  // H2: status correction select — '' means untouched (select shows current).
  const [statusCorrection, setStatusCorrection] = useState('');
  const [loading, setLoading] = useState(true);
  const [applicationFilter, setApplicationFilter] = useState('pending');
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [convertData, setConvertData] = useState({ role: 'driver', department: 'Operations', employee_id: '' });
  
  // Document Signing state
  const [documentSubmissions, setDocumentSubmissions] = useState([]);
  const [documentStats, setDocumentStats] = useState({ totalEmployees: 337, totalSubmissions: 0, completedToday: 0 });
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [downloadingPDF, setDownloadingPDF] = useState(null);
  const [complianceDashboard, setComplianceDashboard] = useState(null);
  
  // New state for additional features
  const [showSignatureModal, setShowSignatureModal] = useState(null);
  const [signatureDetails, setSignatureDetails] = useState(null);
  const [exportingPDFs, setExportingPDFs] = useState(false);
  const [showTestCleanup, setShowTestCleanup] = useState(false);
  const [showEmployeeBrowser, setShowEmployeeBrowser] = useState(false);
  const [selectedComplianceEmployee, setSelectedComplianceEmployee] = useState(null);

  // Document Management state
  const [handbookDoc, setHandbookDoc] = useState(null);
  const [handbookHistory, setHandbookHistory] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadVersion, setUploadVersion] = useState(`${new Date().getFullYear()}.1`);
  const [uploadNotes, setUploadNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const [loadingHandbook, setLoadingHandbook] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // Pending HR filter and Master Employee List state
  const [showPendingHROnly, setShowPendingHROnly] = useState(false);
  const [showMasterEmployeeList, setShowMasterEmployeeList] = useState(false);
  const [masterEmployees, setMasterEmployees] = useState([]);
  const [loadingMasterList, setLoadingMasterList] = useState(false);
  const [masterSearchQuery, setMasterSearchQuery] = useState('');
  // RM1a: the Former employees view. Inactive people keep every signed
  // document; this filter is the only way back to them.
  const [showFormerEmployees, setShowFormerEmployees] = useState(false);

  const { isAdmin } = useAuth();
  const { hasPermission } = usePermissions();
  const [downloadingEmployeePDFs, setDownloadingEmployeePDFs] = useState(null);
  const [employeePDFLinks, setEmployeePDFLinks] = useState({});

  useEffect(() => { fetchData(); }, []);
  useEffect(() => {
    if (activeTab === 'applicants') fetchApplications();
    if (activeTab === 'compliance') { fetchDocumentSubmissions(); fetchComplianceDashboard(); }
    if (activeTab === 'documents') { fetchHandbookDocument(); }
  }, [activeTab, applicationFilter]);
  useEffect(() => { setSearchParams({ tab: activeTab }); }, [activeTab, setSearchParams]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [employeesData, certsData] = await Promise.all([
        employeeService.getAll(),
        certificationService.getExpiring(30)
      ]);
      const employees = employeesData.employees || [];
      setStats({
        total: employees.length,
        drivers: employees.filter(e => e.role === 'driver').length,
        active: employees.filter(e => e.is_active).length,
        newThisMonth: employees.filter(e => {
          const hireDate = new Date(e.hire_date);
          const now = new Date();
          return hireDate.getMonth() === now.getMonth() && hireDate.getFullYear() === now.getFullYear();
        }).length
      });
      setExpiringCerts(certsData);
    } catch (err) { toast.error('Failed to load HR data'); }
    finally { setLoading(false); }
  };

  const fetchApplications = async () => {
    try {
      const data = await applicantService.getAll({ status: applicationFilter !== 'all' ? applicationFilter : undefined });
      setApplications(data.applications || []);
      setApplicationStats(data.statusCounts || {});
    } catch (err) { toast.error('Failed to load applications'); }
  };

  const fetchDocumentSubmissions = async () => {
    try {
      setLoadingDocuments(true);
      const [submissionsRes, statsRes] = await Promise.all([
        api.get('/compliance/submissions'),
        api.get('/compliance/stats')
      ]);
      setDocumentSubmissions(submissionsRes.data.submissions || []);
      setDocumentStats(statsRes.data);
    } catch (err) { console.error('Failed to load document submissions:', err); }
    finally { setLoadingDocuments(false); }
  };

  const fetchComplianceDashboard = async () => {
    try {
      const response = await api.get('/compliance/dashboard');
      setComplianceDashboard(response.data);
    } catch (err) {
      console.error('Failed to load compliance dashboard:', err);
      setComplianceDashboard({
        totalEmployees: 337, fullyCompliant: 0, complianceRate: 0, pendingHRSignatures: 0,
        forms: [
          { id: 1, name: 'Employee Handbook', completed: 0, pendingHR: 0, total: 337, completionRate: 0 },
          { id: 2, name: 'Arbitration Agreement', completed: 0, pendingHR: 0, total: 337, completionRate: 0 }
        ]
      });
    }
  };

  // Fetch master employee list
  const fetchMasterEmployees = async (former = showFormerEmployees) => {
    try {
      setLoadingMasterList(true);
      setEmployeePDFLinks({});
      // RM1a: the default is unchanged ('A'), so every existing caller and the
      // first render are byte-identical to before.
      const response = await api.get(former ? '/compliance/employees?status=inactive' : '/compliance/employees');
      setMasterEmployees(response.data || []);
    } catch (err) {
      console.error('Failed to load master employees:', err);
      toast.error('Failed to load employee list');
    } finally {
      setLoadingMasterList(false);
    }
  };

  // Load PDF links for an employee (stores in state, user clicks individual buttons to open)
  const handleLoadEmployeePDFs = async (emp) => {
    try {
      setDownloadingEmployeePDFs(emp.id);
      const response = await api.get(`/compliance/employees/${emp.id}/pdfs`);
      const { pdfs, count } = response.data;
      if (!pdfs || pdfs.length === 0) {
        toast.info(`No completed PDFs available for ${emp.firstName} ${emp.lastName}`);
        return;
      }
      // Store links in state - user clicks individual buttons to open (avoids popup blocker)
      setEmployeePDFLinks(prev => ({ ...prev, [emp.id]: pdfs }));
      toast.success(`${count} PDF${count > 1 ? 's' : ''} ready — click to open`);
    } catch (err) {
      toast.error('Failed to load PDFs');
      console.error('PDF load error:', err);
    } finally {
      setDownloadingEmployeePDFs(null);
    }
  };

  const fetchHandbookDocument = async () => {
    try {
      setLoadingHandbook(true);
      const response = await api.get('/documents/handbook');
      setHandbookDoc(response.data.document);
    } catch (err) {
      console.error('Failed to load handbook:', err);
      setHandbookDoc(null);
    } finally {
      setLoadingHandbook(false);
    }
  };

  const fetchHandbookHistory = async () => {
    try {
      const response = await api.get('/documents/handbook/history');
      setHandbookHistory(response.data.documents || []);
    } catch (err) {
      console.error('Failed to load handbook history:', err);
    }
  };

  const handleUploadHandbook = async () => {
    if (!uploadFile) {
      toast.error('Please select a PDF file');
      return;
    }
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('documentType', 'handbook');
      formData.append('version', uploadVersion);
      formData.append('notes', uploadNotes);

      await api.post('/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Handbook uploaded successfully!');
      setShowUploadModal(false);
      setUploadFile(null);
      setUploadNotes('');
      fetchHandbookDocument();
      fetchHandbookHistory();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to upload handbook');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadHandbook = async () => {
    if (handbookDoc?.downloadUrl) {
      window.open(handbookDoc.downloadUrl, '_blank');
    }
  };

  const handleDownloadHistoryDoc = async (docId) => {
    try {
      const response = await api.get(`/documents/download/${docId}`);
      if (response.data.downloadUrl) {
        window.open(response.data.downloadUrl, '_blank');
      }
    } catch (err) {
      toast.error('Failed to download document');
    }
  };

  const handleDownloadPDF = async (submission) => {
    try {
      setDownloadingPDF(submission.id);
      const response = await api.get(`/compliance/submissions/${submission.id}/pdf`);
      if (response.data.downloadUrl) {
        window.open(response.data.downloadUrl, '_blank');
        toast.success('PDF opened in new tab');
      } else { toast.error('PDF not available'); }
    } catch (err) { toast.error('Failed to generate PDF'); }
    finally { setDownloadingPDF(null); }
  };

  const handleViewSignature = async (submission) => {
    try {
      setShowSignatureModal(submission);
      const response = await api.get(`/compliance/submissions/${submission.id}/details`);
      setSignatureDetails(response.data);
    } catch (err) { toast.error('Failed to load signature details'); }
  };

  const handleExportAllPDFs = async () => {
    try {
      setExportingPDFs(true);
      toast.info('Preparing ZIP file...');
      const response = await api.get('/compliance/export-pdfs', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `compliance-documents-${new Date().toISOString().split('T')[0]}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDFs exported successfully!');
    } catch (err) { toast.error('Failed to export PDFs'); }
    finally { setExportingPDFs(false); }
  };

  const handlePrintReport = () => {
    window.open('/reports?type=compliance&print=true', '_blank');
  };

  const handleSelectApplication = async (app) => {
    setStatusCorrection('');
    try {
      const fullApp = await applicantService.getById(app.id);
      setSelectedApplication(fullApp);
    } catch (err) { setSelectedApplication(app); }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await applicantService.updateStatus(id, status);
      toast.success(`Application marked as ${status}`);
      fetchApplications();
      setSelectedApplication(null);
    } catch (err) { toast.error('Failed to update status'); }
  };

  const handleDeleteApplication = async () => {
    if (!selectedApplication) return;
    try {
      await applicantService.delete(selectedApplication.id);
      toast.success('Application deleted successfully');
      setShowDeleteConfirm(false);
      setSelectedApplication(null);
      fetchApplications();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to delete application'); }
  };

  const handleConvertToEmployee = async () => {
    if (!selectedApplication) return;
    // P2-B (R9): a typed temporary password mirrors the server policy (12–72); blank → invite email.
    const pw = convertData.password || '';
    if (pw.length > 0 && (pw.length < 12 || pw.length > 72)) { toast.error('Temporary password must be between 12 and 72 characters'); return; }
    try {
      const result = await applicantService.convertToEmployee(selectedApplication.id, { ...convertData, password: pw || undefined });
      toast.success(`${selectedApplication.first_name} ${selectedApplication.last_name} has been hired!`);
      // P2-B (R10): no default password exists any more; the server reports the invite outcome.
      if (result.invite_sent === false) toast.warn(result.message, { autoClose: 10000 });
      else if (result.invite_sent === true) toast.info('Invite email sent — they set their own password from the link.', { autoClose: 8000 });
      setShowConvertModal(false);
      setSelectedApplication(null);
      fetchApplications();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to convert to employee'); }
  };

  const handleViewComplianceEmployee = (emp) => {
    setSelectedComplianceEmployee(emp);
    // Find submissions for this employee
    const empSubmissions = documentSubmissions.filter(s => 
      s.employee_name === `${emp.firstName} ${emp.lastName}`
    );
    if (empSubmissions.length > 0) {
      handleViewSignature(empSubmissions[0]);
    }
  };

  const formatDate = (date) => date ? new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A';
  
  // Fixed: Display times in Eastern Time
  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/New_York'
    });
  };
  
  const getStatusColor = (status) => ({ pending: '#D4A94E', reviewing: '#3b82f6', interview: '#8b5cf6', approved: '#10b981', rejected: '#ef4444', hired: '#22c55e', withdrawn: '#A0A0A0' }[status] || '#A0A0A0');

  // Filter submissions based on pending HR filter
  const displayedSubmissions = showPendingHROnly 
    ? documentSubmissions.filter(s => s.status !== 'completed')
    : documentSubmissions;

  // Filter master employees by search
  const filteredMasterEmployees = masterSearchQuery 
    ? masterEmployees.filter(emp => 
        `${emp.firstName} ${emp.lastName}`.toLowerCase().includes(masterSearchQuery.toLowerCase())
      )
    : masterEmployees;

  return (
    <div style={styles.pageContainer}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div>
            <h1 style={styles.headerTitle}>Human Resources</h1>
            <p style={styles.headerSubtitle}>Manage employees, applications, and compliance</p>
          </div>
          <div style={styles.headerActions}>
            <button style={styles.btnSecondary}>📊 Export Data</button>
            <button style={styles.btnPrimary}>+ New Employee</button>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div style={styles.statsRow}>
        <div style={styles.statCard}>
          <div style={{ ...styles.statIcon, background: 'rgba(37,99,235,0.1)' }}>👥</div>
          <div style={styles.statContent}>
            <div style={styles.statValue}>{stats?.total || 0}</div>
            <div style={styles.statLabel}>Total Employees</div>
          </div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statIcon, background: 'rgba(212,169,78,0.1)' }}>🚌</div>
          <div style={styles.statContent}>
            <div style={styles.statValue}>{stats?.drivers || 0}</div>
            <div style={styles.statLabel}>Active Drivers</div>
          </div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statIcon, background: 'rgba(239,68,68,0.1)' }}>📋</div>
          <div style={styles.statContent}>
            <div style={{ ...styles.statValue, color: applicationStats.pending > 0 ? '#D4A94E' : '#FFFFFF' }}>{applicationStats.pending || 0}</div>
            <div style={styles.statLabel}>Pending Applications</div>
          </div>
        </div>
        <div style={{ ...styles.statCard, borderRight: 'none' }}>
          <div style={{ ...styles.statIcon, background: 'rgba(34,197,94,0.1)' }}>✨</div>
          <div style={styles.statContent}>
            <div style={styles.statValue}>{stats?.newThisMonth || 0}</div>
            <div style={styles.statLabel}>New This Month</div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={styles.mainContent}>
        {/* Tabs */}
        <div style={styles.tabsContainer}>
          {['overview', 'applicants', 'compliance', 'training', 'documents'].map(tab => (
            <button key={tab} style={{ ...styles.tab, ...(activeTab === tab ? styles.tabActive : {}) }} onClick={() => setActiveTab(tab)}>
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              {tab === 'applicants' && applicationStats.pending > 0 && (
                <span style={{ marginLeft: '6px', background: '#F87171', color: '#1A1A1A', padding: '2px 6px', borderRadius: '10px', fontSize: '11px' }}>{applicationStats.pending}</span>
              )}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div style={styles.gridTwo}>
            <div style={styles.card}>
              <div style={styles.cardHeader}><h3 style={styles.cardTitle}>⚠️ Action Required</h3></div>
              <div style={styles.cardBody}>
                {applicationStats.pending > 0 && (
                  <div onClick={() => setActiveTab('applicants')} style={{ display: 'flex', alignItems: 'center', padding: '16px', background: 'rgba(212,169,78,0.1)', borderRadius: '8px', marginBottom: '12px', cursor: 'pointer' }}>
                    <span style={{ fontSize: '24px', marginRight: '16px' }}>📋</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', color: '#D4A94E' }}>{applicationStats.pending} pending applications</div>
                      <div style={{ fontSize: '13px', color: '#C49A3E' }}>Review and process new applicants</div>
                    </div>
                    <span style={{ color: '#D4A94E' }}>→</span>
                  </div>
                )}
                {expiringCerts?.total > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', padding: '16px', background: 'rgba(239,68,68,0.1)', borderRadius: '8px' }}>
                    <span style={{ fontSize: '24px', marginRight: '16px' }}>📜</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', color: '#F87171' }}>{expiringCerts.total} certifications expiring</div>
                      <div style={{ fontSize: '13px', color: '#F87171' }}>Within the next 30 days</div>
                    </div>
                  </div>
                )}
                {!applicationStats.pending && !expiringCerts?.total && (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#A0A0A0' }}>
                    <span style={{ fontSize: '48px' }}>✅</span>
                    <p style={{ marginTop: '12px' }}>No urgent actions required</p>
                  </div>
                )}
              </div>
            </div>
            <div style={styles.card}>
              <div style={styles.cardHeader}><h3 style={styles.cardTitle}>⚡ Quick Actions</h3></div>
              <div style={styles.cardBody}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  {[
                    { icon: '📋', label: 'Review Applications', action: () => setActiveTab('applicants') },
                    { icon: '👥', label: 'View Employees', action: () => window.location.href = '/employees' },
                    { icon: '✍️', label: 'Document Compliance', action: () => setActiveTab('compliance') },
                    { icon: '📁', label: 'HR Documents', action: () => setActiveTab('documents') },
                  ].map((item, i) => (
                    <button key={i} onClick={item.action} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px', background: '#141414', border: '1px solid #2A2A2A', borderRadius: '10px', cursor: 'pointer' }}>
                      <span style={{ fontSize: '24px' }}>{item.icon}</span>
                      <span style={{ fontWeight: '500', color: '#E0E0E0' }}>{item.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Applicants Tab */}
        {activeTab === 'applicants' && (
          <div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
              {['all', 'pending', 'reviewing', 'interview', 'approved', 'rejected', 'hired'].map(status => (
                <button key={status} onClick={() => setApplicationFilter(status)} style={{ padding: '8px 16px', borderRadius: '20px', border: 'none', cursor: 'pointer', background: applicationFilter === status ? '#FFFFFF' : '#1E1E1E', color: applicationFilter === status ? '#1A1A1A' : '#A0A0A0', fontWeight: '500', fontSize: '13px', boxShadow: '0 1px 2px rgba(0,0,0,0.2)', textTransform: 'capitalize' }}>
                  {status} {applicationStats[status] ? `(${applicationStats[status]})` : ''}
                </button>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: selectedApplication ? '1fr 400px' : '1fr', gap: '24px' }}>
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <h3 style={styles.cardTitle}>Applications</h3>
                  <span style={{ fontSize: '13px', color: '#A0A0A0' }}>{applications.length} results</span>
                </div>
                {applications.length === 0 ? (
                  <div style={styles.emptyState}><span style={{ fontSize: '48px' }}>📭</span><p>No {applicationFilter !== 'all' ? applicationFilter : ''} applications found</p></div>
                ) : (
                  <table style={styles.table}>
                    <thead style={styles.tableHeader}>
                      <tr><th style={styles.th}>Applicant</th><th style={styles.th}>Position</th><th style={styles.th}>CDL</th><th style={styles.th}>Status</th><th style={styles.th}>Applied</th></tr>
                    </thead>
                    <tbody>
                      {applications.map(app => (
                        <tr key={app.id} onClick={() => handleSelectApplication(app)} style={{ cursor: 'pointer', background: selectedApplication?.id === app.id ? 'rgba(212,169,78,0.1)' : 'transparent' }}>
                          <td style={styles.td}><strong>{app.first_name} {app.last_name}</strong></td>
                          <td style={styles.td}>{app.position}</td>
                          <td style={styles.td}><span style={{ ...styles.badge, background: app.has_cdl ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)', color: app.has_cdl ? '#4ADE80' : '#F87171' }}>{app.has_cdl ? `✓ ${app.cdl_class || 'CDL'}` : '✗ No'}</span></td>
                          <td style={styles.td}><span style={{ ...styles.badge, background: getStatusColor(app.status) + '20', color: getStatusColor(app.status) }}>{app.status}</span></td>
                          <td style={styles.td}>{formatDate(app.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              {selectedApplication && (
                <div style={{ ...styles.card, position: 'sticky', top: '24px', alignSelf: 'start' }}>
                  <div style={{ ...styles.cardHeader, background: '#1A1A1A' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '18px', color: '#FFFFFF' }}>{selectedApplication.first_name} {selectedApplication.last_name}</h3>
                      <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#A0A0A0' }}>{selectedApplication.position}</p>
                    </div>
                    <button onClick={() => setSelectedApplication(null)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6B6B6B' }}>×</button>
                  </div>
                  <div style={{ padding: '20px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                      <div><div style={{ fontSize: '11px', color: '#6B6B6B', fontWeight: '600', marginBottom: '4px' }}>EMAIL</div><div style={{ fontSize: '14px', color: '#E0E0E0' }}>{selectedApplication.email}</div></div>
                      <div><div style={{ fontSize: '11px', color: '#6B6B6B', fontWeight: '600', marginBottom: '4px' }}>PHONE</div><div style={{ fontSize: '14px', color: '#E0E0E0' }}>{selectedApplication.phone || 'N/A'}</div></div>
                      <div><div style={{ fontSize: '11px', color: '#6B6B6B', fontWeight: '600', marginBottom: '4px' }}>EXPERIENCE</div><div style={{ fontSize: '14px', color: '#E0E0E0' }}>{selectedApplication.experience_years || 0} years</div></div>
                      <div><div style={{ fontSize: '11px', color: '#6B6B6B', fontWeight: '600', marginBottom: '4px' }}>CDL STATUS</div><div style={{ fontSize: '14px', color: '#E0E0E0' }}>{selectedApplication.has_cdl ? `Class ${selectedApplication.cdl_class || 'N/A'}` : 'No CDL'}</div></div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingTop: '16px', borderTop: '1px solid #2A2A2A' }}>
                      {selectedApplication.status === 'pending' && <button style={{ ...styles.btnPrimary, justifyContent: 'center' }} onClick={() => handleStatusChange(selectedApplication.id, 'reviewing')}>Start Review</button>}
                      {['pending', 'reviewing'].includes(selectedApplication.status) && <button style={{ ...styles.btnSecondary, justifyContent: 'center', background: 'rgba(139,92,246,0.15)', color: '#A78BFA', border: '1px solid rgba(139,92,246,0.3)' }} onClick={() => handleStatusChange(selectedApplication.id, 'interview')}>Schedule Interview</button>}
                      {['reviewing', 'interview'].includes(selectedApplication.status) && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                          <button style={{ ...styles.btnSecondary, justifyContent: 'center', background: 'rgba(16,185,129,0.15)', color: '#4ADE80', border: '1px solid rgba(16,185,129,0.3)' }} onClick={() => handleStatusChange(selectedApplication.id, 'approved')}>✓ Approve</button>
                          <button style={{ ...styles.btnSecondary, justifyContent: 'center', background: 'rgba(239,68,68,0.15)', color: '#F87171', border: '1px solid rgba(239,68,68,0.3)' }} onClick={() => handleStatusChange(selectedApplication.id, 'rejected')}>✗ Reject</button>
                        </div>
                      )}
                      {selectedApplication.status === 'approved' && <button style={{ ...styles.btnPrimary, justifyContent: 'center', background: 'rgba(34,197,94,0.15)', color: '#4ADE80', border: '1px solid rgba(34,197,94,0.3)' }} onClick={() => setShowConvertModal(true)}>Hire Employee</button>}
                      <button style={{ ...styles.btnSecondary, justifyContent: 'center', color: '#F87171', borderColor: 'rgba(239,68,68,0.3)' }} onClick={() => setShowDeleteConfirm(true)}>Delete Application</button>
                      {/* H2: correction affordance — the forward buttons stay the
                          workflow guidance; this is the deliberate any-direction
                          fix (gate-ratified). 'hired' excluded: hiring is the
                          convert flow, not a status write. Server enforces the
                          role gate; the select calls the same endpoint. */}
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', paddingTop: '12px', borderTop: '1px solid #2A2A2A' }}>
                        <span style={{ fontSize: '11px', color: '#6B6B6B', fontWeight: '600', whiteSpace: 'nowrap' }}>CORRECT STATUS</span>
                        <select
                          value={statusCorrection || selectedApplication.status}
                          onChange={(e) => setStatusCorrection(e.target.value)}
                          style={{ flex: 1, background: '#1A1A1A', color: '#E0E0E0', border: '1px solid #2A2A2A', borderRadius: '6px', padding: '6px 8px', fontSize: '13px' }}
                        >
                          {['pending', 'reviewing', 'interview', 'approved', 'rejected', 'withdrawn'].map((s) => (
                            <option key={s} value={s}>{s}{s === selectedApplication.status ? ' (current)' : ''}</option>
                          ))}
                        </select>
                        <button
                          style={{ ...styles.btnSecondary, justifyContent: 'center', opacity: (!statusCorrection || statusCorrection === selectedApplication.status) ? 0.5 : 1 }}
                          disabled={!statusCorrection || statusCorrection === selectedApplication.status}
                          onClick={() => handleStatusChange(selectedApplication.id, statusCorrection)}
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Compliance Tab */}
        {activeTab === 'compliance' && (
          <div>
            {/* Dashboard Cards */}
            <div style={{ ...styles.gridTwo, marginBottom: '24px' }}>
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <h3 style={styles.cardTitle}>📊 Overall Compliance</h3>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={handlePrintReport} style={{ ...styles.btnSecondary, ...styles.btnSmall }}>🖨️ Print Report</button>
                    <a href="/reports" style={{ fontSize: '13px', color: '#60A5FA', textDecoration: 'none', padding: '6px 0' }}>View Full →</a>
                  </div>
                </div>
                <div style={{ ...styles.cardBody, display: 'flex', alignItems: 'center', justifyContent: 'space-around' }}>
                  {complianceDashboard ? (
                    <>
                      <DonutChart percentage={complianceDashboard.complianceRate} />
                      <div style={{ display: 'grid', gap: '12px' }}>
                        <div style={{ padding: '16px 24px', background: '#F5F2EB', borderRadius: '10px', textAlign: 'center' }}>
                          <div style={{ fontSize: '28px', fontWeight: '700', color: '#1A1A1A' }}>{complianceDashboard.fullyCompliant}</div>
                          <div style={{ fontSize: '12px', color: '#1A1A1A' }}>Fully Compliant</div>
                        </div>
                        <div style={{ padding: '16px 24px', background: '#F5F2EB', borderRadius: '10px', textAlign: 'center' }}>
                          <div style={{ fontSize: '28px', fontWeight: '700', color: '#1A1A1A' }}>{complianceDashboard.totalEmployees - complianceDashboard.fullyCompliant}</div>
                          <div style={{ fontSize: '12px', color: '#1A1A1A' }}>Incomplete</div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#6B6B6B' }}>Loading...</div>
                  )}
                </div>
              </div>
              <div style={styles.card}>
                <div style={styles.cardHeader}>
                  <h3 style={styles.cardTitle}>📝 Document Progress</h3>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={handleExportAllPDFs} disabled={exportingPDFs} style={{ ...styles.btnSecondary, ...styles.btnSmall }}>{exportingPDFs ? '⏳...' : '📦 Export All PDFs'}</button>
                    <a href="https://www.gvcbus.com/compliance" target="_blank" rel="noopener noreferrer" style={{ ...styles.btnSecondary, ...styles.btnSmall, textDecoration: 'none' }}>Open Kiosk →</a>
                  </div>
                </div>
                <div style={styles.cardBody}>
                  {complianceDashboard?.forms?.map(form => (
                    <ProgressBar key={form.id} label={form.name} percentage={form.completionRate} completed={form.completed} total={form.total} />
                  ))}
                  {complianceDashboard?.pendingHRSignatures > 0 && (
                    <div 
                      onClick={() => setShowPendingHROnly(!showPendingHROnly)}
                      style={{ marginTop: '16px', padding: '12px 16px', background: showPendingHROnly ? '#D4A94E' : 'rgba(212,169,78,0.1)', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', transition: 'all 0.2s ease', border: showPendingHROnly ? '2px solid #D4A94E' : '2px solid transparent' }}
                    >
                      <span style={{ fontSize: '20px' }}>⏳</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '600', color: showPendingHROnly ? '#1A1A1A' : '#D4A94E', fontSize: '14px' }}>{complianceDashboard.pendingHRSignatures} awaiting HR witness</div>
                        <div style={{ fontSize: '12px', color: showPendingHROnly ? '#1A1A1A' : '#C49A3E' }}>{showPendingHROnly ? '✓ Showing pending only - Click to show all' : 'Click to filter & view pending submissions'}</div>
                      </div>
                      <span style={{ color: showPendingHROnly ? '#1A1A1A' : '#D4A94E', fontSize: '18px' }}>→</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Employee Browser Card */}
            <div style={{ ...styles.card, marginBottom: '24px' }}>
              <div style={styles.cardHeader}>
                <h3 style={styles.cardTitle}>👥 Employee Compliance Status</h3>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => { setShowMasterEmployeeList(true); fetchMasterEmployees(); }} style={{ ...styles.btnSecondary, ...styles.btnSmall }}>📋 Master List</button>
                  <button onClick={() => setShowEmployeeBrowser(!showEmployeeBrowser)} style={{ ...styles.btnSecondary, ...styles.btnSmall }}>{showEmployeeBrowser ? '▲ Hide' : '▼ Browse All Employees'}</button>
                </div>
              </div>
              {showEmployeeBrowser && (
                <div style={styles.cardBody}>
                  <EmployeeBrowser onViewEmployee={handleViewComplianceEmployee} />
                </div>
              )}
            </div>

            {/* RM1a: roster reconciliation, gated on hr_compliance:manage. An
                admin bypasses permissions entirely, so Michael Townsend sees it
                without a grant; the GM has it from the boot seed. */}
            {(isAdmin || hasPermission('hr_compliance', 'manage')) && (
              <RosterUpload onApplied={() => { fetchComplianceDashboard(); fetchDocumentSubmissions(); }} />
            )}

            {/* Recent Submissions */}
            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <h3 style={styles.cardTitle}>
                  ✍️ {showPendingHROnly ? 'Pending HR Submissions' : 'Recent Submissions'}
                  {showPendingHROnly && <span style={{ marginLeft: '8px', fontSize: '12px', color: '#D4A94E', fontWeight: 'normal' }}>(filtered)</span>}
                </h3>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                  {showPendingHROnly && (
                    <button onClick={() => setShowPendingHROnly(false)} style={{ ...styles.btnSecondary, ...styles.btnSmall, background: 'rgba(212,169,78,0.1)', borderColor: '#D4A94E' }}>✕ Clear Filter</button>
                  )}
                  <span style={{ fontSize: '13px', color: '#4ADE80', fontWeight: '600' }}>{documentStats.completedToday || 0} today</span>
                  <span style={{ fontSize: '13px', color: '#A0A0A0' }}>{documentStats.totalSubmissions || 0} total</span>
                </div>
              </div>
              {displayedSubmissions.length === 0 ? (
                <div style={styles.emptyState}>
                  <span style={{ fontSize: '48px' }}>{showPendingHROnly ? '✅' : '📭'}</span>
                  <p>{showPendingHROnly ? 'No pending HR submissions!' : 'No document submissions yet'}</p>
                  <p style={{ fontSize: '13px', color: '#6B6B6B' }}>{showPendingHROnly ? 'All submissions have been witnessed' : 'Employees can sign documents at the compliance kiosk'}</p>
                </div>
              ) : (
                <table style={styles.table}>
                  <thead style={styles.tableHeader}>
                    <tr><th style={styles.th}>Employee</th><th style={styles.th}>Document</th><th style={styles.th}>Status</th><th style={styles.th}>Date</th><th style={styles.th}>Actions</th></tr>
                  </thead>
                  <tbody>
                    {displayedSubmissions.slice(0, showPendingHROnly ? 50 : 10).map(sub => (
                      <tr key={sub.id} style={{ background: sub.status !== 'completed' ? 'rgba(212,169,78,0.05)' : 'transparent' }}>
                        <td style={styles.td}><strong>{sub.employee_name}</strong></td>
                        <td style={styles.td}><span style={{ ...styles.badge, background: sub.form_name?.includes('Handbook') ? 'rgba(37,99,235,0.1)' : 'rgba(212,169,78,0.1)', color: sub.form_name?.includes('Handbook') ? '#60A5FA' : '#D4A94E' }}>{sub.form_name?.includes('Handbook') ? '📘' : '⚖️'} {sub.form_name?.replace('Employee ', '').replace(' Agreement', '')}</span></td>
                        <td style={styles.td}><span style={{ ...styles.badge, background: sub.status === 'completed' ? 'rgba(34,197,94,0.1)' : 'rgba(212,169,78,0.1)', color: sub.status === 'completed' ? '#4ADE80' : '#D4A94E' }}>{sub.status === 'completed' ? '✓ Complete' : '⏳ Pending HR'}</span></td>
                        <td style={styles.td}>{formatDateTime(sub.signed_at)}</td>
                        <td style={styles.td}>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button onClick={() => handleViewSignature(sub)} style={{ padding: '4px 10px', background: 'rgba(139,92,246,0.15)', color: '#A78BFA', border: '1px solid rgba(139,92,246,0.3)', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>View</button>
                            {sub.status === 'completed' && <button onClick={() => handleDownloadPDF(sub)} disabled={downloadingPDF === sub.id} style={{ padding: '4px 10px', background: 'rgba(59,130,246,0.15)', color: '#60A5FA', border: '1px solid rgba(59,130,246,0.3)', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>{downloadingPDF === sub.id ? '...' : 'PDF'}</button>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Training Tab */}
        {activeTab === 'training' && (
          <div style={styles.card}>
            <div style={styles.emptyState}>
              <span style={{ fontSize: '64px' }}>🎓</span>
              <h3 style={{ marginTop: '16px', color: '#FFFFFF' }}>Training Management</h3>
              <p style={{ color: '#E0E0E0' }}>Track employee training and development programs</p>
              <p style={{ fontSize: '13px', color: '#6B6B6B' }}>Visit ABC School for CDL training management</p>
            </div>
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div>
            <div style={styles.card}>
              <div style={styles.cardHeader}><h3 style={styles.cardTitle}>📄 Company Documents</h3></div>
              <div style={styles.cardBody}>
                {loadingHandbook ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#6B6B6B' }}>Loading...</div>
                ) : (
                  <div style={{ display: 'grid', gap: '16px' }}>
                    <div style={{ border: '1px solid #2A2A2A', borderRadius: '12px', padding: '20px', display: 'flex', alignItems: 'center', gap: '20px' }}>
                      <div style={{ width: '64px', height: '64px', background: 'rgba(212,169,78,0.1)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '32px' }}>📘</div>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ margin: '0 0 4px', fontSize: '18px', color: '#FFFFFF' }}>Employee Handbook</h4>
                        {handbookDoc ? (
                          <>
                            <p style={{ margin: '0 0 4px', fontSize: '14px', color: '#A0A0A0' }}>Version: <strong>{handbookDoc.version || 'N/A'}</strong> • Updated: <strong>{formatDate(handbookDoc.uploadedAt)}</strong></p>
                            <p style={{ margin: 0, fontSize: '13px', color: '#6B6B6B' }}>{handbookDoc.fileName} • {Math.round((handbookDoc.fileSize || 0) / 1024)} KB</p>
                          </>
                        ) : (
                          <p style={{ margin: 0, fontSize: '14px', color: '#D4A94E' }}>No handbook uploaded yet</p>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {handbookDoc && <button onClick={handleDownloadHandbook} style={{ ...styles.btnSecondary }}>⬇️ Download Current</button>}
                        <button onClick={() => setShowUploadModal(true)} style={styles.btnPrimary}>📤 Upload New Version</button>
                      </div>
                    </div>
                    <div style={{ marginTop: '8px' }}>
                      <button onClick={() => { setShowHistory(!showHistory); if (!showHistory) fetchHandbookHistory(); }} style={{ background: 'none', border: 'none', color: '#60A5FA', cursor: 'pointer', fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        {showHistory ? '▼' : '▶'} Version History
                      </button>
                      {showHistory && (
                        <div style={{ marginTop: '12px', paddingLeft: '16px' }}>
                          {handbookHistory.length === 0 ? (
                            <p style={{ color: '#6B6B6B', fontSize: '13px' }}>No version history available</p>
                          ) : (
                            <table style={{ ...styles.table, fontSize: '13px' }}>
                              <thead style={styles.tableHeader}>
                                <tr><th style={styles.th}>Version</th><th style={styles.th}>File</th><th style={styles.th}>Uploaded By</th><th style={styles.th}>Date</th><th style={styles.th}>Status</th><th style={styles.th}>Action</th></tr>
                              </thead>
                              <tbody>
                                {handbookHistory.map(doc => (
                                  <tr key={doc.id}>
                                    <td style={styles.td}><strong>{doc.version || 'N/A'}</strong></td>
                                    <td style={styles.td}>{doc.fileName}</td>
                                    <td style={styles.td}>{doc.uploadedByName || 'Unknown'}</td>
                                    <td style={styles.td}>{formatDate(doc.uploadedAt)}</td>
                                    <td style={styles.td}><span style={{ ...styles.badge, background: doc.isActive ? 'rgba(34,197,94,0.1)' : '#2A2A2A', color: doc.isActive ? '#4ADE80' : '#A0A0A0' }}>{doc.isActive ? '✓ Active' : 'Archived'}</span></td>
                                    <td style={styles.td}><button onClick={() => handleDownloadHistoryDoc(doc.id)} style={{ ...styles.btnSecondary, ...styles.btnSmall }}>⬇️ Download</button></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div style={{ ...styles.card, marginTop: '24px' }}>
              <div style={styles.cardBody}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                  <span style={{ fontSize: '24px' }}>💡</span>
                  <div>
                    <h4 style={{ margin: '0 0 8px', color: '#FFFFFF' }}>About Document Management</h4>
                    <p style={{ margin: 0, fontSize: '14px', color: '#A0A0A0', lineHeight: '1.6' }}>Upload the official Employee Handbook PDF here. The uploaded handbook will be available for:</p>
                    <ul style={{ margin: '8px 0 0', paddingLeft: '20px', color: '#A0A0A0', fontSize: '14px', lineHeight: '1.8' }}>
                      <li>Download on the public website at <a href="https://www.gvcbus.com/employee-portal" target="_blank" rel="noopener noreferrer">gvcbus.com/employee-portal</a></li>
                      <li>Employee access via the mobile app</li>
                      <li>Reference in the compliance kiosk</li>
                    </ul>
                    <p style={{ margin: '12px 0 0', fontSize: '13px', color: '#6B6B6B' }}>When you upload a new version, the previous version is automatically archived.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showConvertModal && selectedApplication && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h2 style={{ margin: '0 0 8px', color: '#FFFFFF' }}>🎉 Hire {selectedApplication.first_name}</h2>
            <p style={{ color: '#A0A0A0', marginBottom: '24px' }}>Convert this application to an employee record</p>
            <div style={{ marginBottom: '16px' }}><label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#E0E0E0' }}>Role</label><select value={convertData.role} onChange={e => setConvertData({...convertData, role: e.target.value})} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #2A2A2A', background: '#141414', color: '#FFFFFF' }}><option value="driver">Driver</option><option value="employee">Employee</option><option value="dispatcher">Dispatcher</option></select></div>
            <div style={{ marginBottom: '16px' }}><label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#E0E0E0' }}>Department</label><input type="text" value={convertData.department} onChange={e => setConvertData({...convertData, department: e.target.value})} style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #2A2A2A', background: '#141414', color: '#FFFFFF' }} /></div>
            <div style={{ marginBottom: '16px' }}><label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#E0E0E0' }}>Employee ID (Optional)</label><input type="text" value={convertData.employee_id} onChange={e => setConvertData({...convertData, employee_id: e.target.value})} placeholder="e.g., GVC-001" style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #2A2A2A', background: '#141414', color: '#FFFFFF' }} /></div>
            {/* P2-B (R9/R10): optional temporary password; blank → the applicant gets an invite link at their application email */}
            <div style={{ marginBottom: '24px' }}><label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '14px', color: '#E0E0E0' }}>Temporary password (leave blank to email an invite)</label><input type="text" autoComplete="off" value={convertData.password || ''} onChange={e => setConvertData({...convertData, password: e.target.value})} placeholder="12–72 characters, or leave blank" style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #2A2A2A', background: '#141414', color: '#FFFFFF' }} /><small style={{ display: 'block', marginTop: '6px', fontSize: '12px', color: '#A0A0A0' }}>Blank = the new hire gets an invite link at {selectedApplication.email}. A typed password must be changed at their first sign-in.</small></div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setShowConvertModal(false)} style={{ ...styles.btnSecondary, flex: 1, justifyContent: 'center' }}>Cancel</button>
              <button onClick={handleConvertToEmployee} style={{ ...styles.btnPrimary, flex: 1, justifyContent: 'center', background: 'rgba(34,197,94,0.15)', color: '#4ADE80', border: '1px solid rgba(34,197,94,0.3)' }}>Create Employee</button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && selectedApplication && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <h2 style={{ margin: '0 0 8px', color: '#F87171' }}>🗑️ Delete Application?</h2>
            <p style={{ color: '#A0A0A0', marginBottom: '24px' }}>Are you sure you want to delete the application from <strong style={{ color: '#E0E0E0' }}>{selectedApplication.first_name} {selectedApplication.last_name}</strong>?</p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setShowDeleteConfirm(false)} style={{ ...styles.btnSecondary, flex: 1, justifyContent: 'center' }}>Cancel</button>
              <button onClick={handleDeleteApplication} style={{ ...styles.btnSecondary, flex: 1, justifyContent: 'center', background: 'rgba(239,68,68,0.15)', color: '#F87171', border: '1px solid rgba(239,68,68,0.3)' }}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Signature Details Modal */}
      {showSignatureModal && (
        <div style={styles.modal}>
          <div style={{ ...styles.modalContent, width: '600px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0, color: '#FFFFFF' }}>📝 Signature Details</h2>
              <button onClick={() => { setShowSignatureModal(null); setSignatureDetails(null); }} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6B6B6B' }}>×</button>
            </div>
            {signatureDetails ? (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                  <div><div style={{ fontSize: '11px', color: '#6B6B6B', fontWeight: '600', marginBottom: '4px' }}>EMPLOYEE</div><div style={{ fontSize: '16px', fontWeight: '600', color: '#FFFFFF' }}>{signatureDetails.employeeName}</div></div>
                  <div><div style={{ fontSize: '11px', color: '#6B6B6B', fontWeight: '600', marginBottom: '4px' }}>DOCUMENT</div><div style={{ fontSize: '16px', color: '#E0E0E0' }}>{signatureDetails.formName}</div></div>
                  <div><div style={{ fontSize: '11px', color: '#6B6B6B', fontWeight: '600', marginBottom: '4px' }}>PRINTED NAME</div><div style={{ fontSize: '16px', color: '#E0E0E0' }}>{signatureDetails.employeePrintedName}</div></div>
                  <div><div style={{ fontSize: '11px', color: '#6B6B6B', fontWeight: '600', marginBottom: '4px' }}>SIGNED AT</div><div style={{ fontSize: '16px', color: '#E0E0E0' }}>{formatDateTime(signatureDetails.employeeSignedAt)}</div></div>
                  <div><div style={{ fontSize: '11px', color: '#6B6B6B', fontWeight: '600', marginBottom: '4px' }}>HR WITNESS</div><div style={{ fontSize: '16px', color: '#E0E0E0' }}>{signatureDetails.hrRepName || 'Pending'}</div></div>
                  <div><div style={{ fontSize: '11px', color: '#6B6B6B', fontWeight: '600', marginBottom: '4px' }}>HR SIGNED AT</div><div style={{ fontSize: '16px', color: '#E0E0E0' }}>{signatureDetails.hrSignedAt ? formatDateTime(signatureDetails.hrSignedAt) : 'Pending'}</div></div>
                </div>
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '11px', color: '#6B6B6B', fontWeight: '600', marginBottom: '8px' }}>EMPLOYEE SIGNATURE</div>
                  {signatureDetails.employeeSignature ? (
                    <div style={{ background: '#141414', border: '1px solid #2A2A2A', borderRadius: '8px', padding: '16px', textAlign: 'center' }}>
                      <img src={signatureDetails.employeeSignature} alt="Employee Signature" style={{ maxWidth: '100%', maxHeight: '100px' }} />
                    </div>
                  ) : <div style={{ color: '#6B6B6B' }}>No signature image</div>}
                </div>
                {signatureDetails.hrSignature && (
                  <div>
                    <div style={{ fontSize: '11px', color: '#6B6B6B', fontWeight: '600', marginBottom: '8px' }}>HR WITNESS SIGNATURE</div>
                    <div style={{ background: '#141414', border: '1px solid #2A2A2A', borderRadius: '8px', padding: '16px', textAlign: 'center' }}>
                      <img src={signatureDetails.hrSignature} alt="HR Signature" style={{ maxWidth: '100%', maxHeight: '100px' }} />
                    </div>
                  </div>
                )}
                <div style={{ marginTop: '24px', padding: '12px', background: '#141414', borderRadius: '8px', fontSize: '12px', color: '#A0A0A0' }}>
                  <strong>Consent:</strong> {signatureDetails.consentRead ? '✓ Read handbook' : '✗'} | {signatureDetails.consentElectronic ? '✓ Electronic signature' : '✗'}<br />
                  <strong>IP Address:</strong> {signatureDetails.employeeIp || 'N/A'}
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6B6B6B' }}>Loading...</div>
            )}
          </div>
        </div>
      )}

      {/* Upload Handbook Modal */}
      {showUploadModal && (
        <div style={styles.modal}>
          <div style={styles.modalContent}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0, color: '#FFFFFF' }}>📤 Upload Employee Handbook</h2>
              <button onClick={() => setShowUploadModal(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6B6B6B' }}>×</button>
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: '#E0E0E0' }}>PDF File *</label>
              <div style={{ border: '2px dashed #2A2A2A', borderRadius: '12px', padding: '32px', textAlign: 'center', background: uploadFile ? 'rgba(34,197,94,0.1)' : '#141414', cursor: 'pointer' }} onClick={() => document.getElementById('handbook-upload').click()}>
                <input id="handbook-upload" type="file" accept=".pdf" style={{ display: 'none' }} onChange={(e) => setUploadFile(e.target.files[0])} />
                {uploadFile ? (
                  <div><span style={{ fontSize: '32px' }}>📄</span><p style={{ margin: '8px 0 0', fontWeight: '500', color: '#4ADE80' }}>{uploadFile.name}</p><p style={{ margin: '4px 0 0', fontSize: '13px', color: '#A0A0A0' }}>{Math.round(uploadFile.size / 1024)} KB</p></div>
                ) : (
                  <div><span style={{ fontSize: '32px' }}>📁</span><p style={{ margin: '8px 0 0', color: '#A0A0A0' }}>Click to select PDF file</p><p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6B6B6B' }}>Max 50MB</p></div>
                )}
              </div>
            </div>
            <div style={{ marginBottom: '20px' }}><label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: '#E0E0E0' }}>Version</label><input type="text" value={uploadVersion} onChange={(e) => setUploadVersion(e.target.value)} placeholder="e.g., 2025.1" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #2A2A2A', fontSize: '14px', background: '#141414', color: '#FFFFFF' }} /></div>
            <div style={{ marginBottom: '24px' }}><label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: '#E0E0E0' }}>Notes (optional)</label><textarea value={uploadNotes} onChange={(e) => setUploadNotes(e.target.value)} placeholder="Any notes about this version..." rows={3} style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #2A2A2A', fontSize: '14px', resize: 'vertical', background: '#141414', color: '#FFFFFF' }} /></div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setShowUploadModal(false)} style={{ ...styles.btnSecondary, flex: 1, justifyContent: 'center' }}>Cancel</button>
              <button onClick={handleUploadHandbook} disabled={!uploadFile || uploading} style={{ ...styles.btnPrimary, flex: 1, justifyContent: 'center', opacity: (!uploadFile || uploading) ? 0.6 : 1, cursor: (!uploadFile || uploading) ? 'not-allowed' : 'pointer' }}>{uploading ? '⏳ Uploading...' : '📤 Upload Handbook'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Master Employee List Modal - with per-form PDF Download buttons */}
      {showMasterEmployeeList && (
        <div style={styles.modal}>
          <div style={{ ...styles.modalContent, width: '900px', maxHeight: '85vh' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <h2 style={{ margin: 0, color: '#FFFFFF' }}>📋 Master Employee List</h2>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#A0A0A0' }}>{masterEmployees.length} {showFormerEmployees ? 'former employees • their signed documents are kept' : 'employees in compliance tracking'} • Search and download compliance PDFs</p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <button onClick={() => { const next = !showFormerEmployees; setShowFormerEmployees(next); fetchMasterEmployees(next); }}
                  style={{ padding: '6px 12px', borderRadius: '8px', fontSize: '12px', cursor: 'pointer',
                    background: showFormerEmployees ? 'rgba(212,169,78,0.15)' : 'transparent',
                    color: showFormerEmployees ? '#D4A94E' : '#A0A0A0',
                    border: `1px solid ${showFormerEmployees ? '#D4A94E' : '#2A2A2A'}` }}>
                  {showFormerEmployees ? '← Current employees' : 'Former employees'}
                </button>
                <button onClick={() => setShowMasterEmployeeList(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6B6B6B' }}>×</button>
              </div>
            </div>

            <div style={{ marginBottom: '16px', position: 'relative' }}>
              <input type="text" placeholder="Search employees..." value={masterSearchQuery} onChange={(e) => setMasterSearchQuery(e.target.value)} style={{ width: '100%', padding: '10px 12px 10px 36px', border: '1px solid #2A2A2A', borderRadius: '8px', fontSize: '14px', outline: 'none', background: '#141414', color: '#FFFFFF' }} />
              <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6B6B6B' }}>🔍</span>
            </div>
            
            {loadingMasterList ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6B6B6B' }}>Loading employees...</div>
            ) : (
              <div style={{ maxHeight: '55vh', overflowY: 'auto', border: '1px solid #2A2A2A', borderRadius: '8px' }}>
                <table style={styles.table}>
                  <thead style={{ ...styles.tableHeader, position: 'sticky', top: 0, zIndex: 1, background: '#1A1A1A' }}>
                    <tr>
                      <th style={styles.th}>Employee Name</th>
                      <th style={styles.th}>Status</th>
                      <th style={styles.th}>Forms</th>
                      <th style={styles.th}>Source</th>
                      <th style={styles.th}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMasterEmployees.length === 0 ? (
                      <tr>
                        <td colSpan="5" style={{ ...styles.td, textAlign: 'center', padding: '40px', color: '#6B6B6B' }}>
                          {masterSearchQuery ? 'No employees match your search' : 'No employees found'}
                        </td>
                      </tr>
                    ) : (
                      filteredMasterEmployees.map(emp => (
                        <tr key={emp.id}>
                          <td style={styles.td}><strong>{emp.lastName}, {emp.firstName}</strong></td>
                          <td style={styles.td}>
                            <span style={{ ...styles.badge, background: emp.isComplete ? 'rgba(34,197,94,0.1)' : emp.overallStatus === 'pending_hr' ? 'rgba(212,169,78,0.1)' : '#2A2A2A', color: emp.isComplete ? '#4ADE80' : emp.overallStatus === 'pending_hr' ? '#D4A94E' : '#A0A0A0' }}>
                              {emp.isComplete ? '✓ Complete' : emp.overallStatus === 'pending_hr' ? '⏳ Pending HR' : 'Incomplete'}
                            </span>
                          </td>
                          <td style={styles.td}>
                            {emp.forms?.map(f => (
                              <span key={f.formId} style={{ marginRight: '8px', fontSize: '12px' }}>
                                {f.status === 'completed' ? '✅' : f.status === 'employee_signed' ? '⏳' : '○'}
                              </span>
                            ))}
                          </td>
                          <td style={styles.td}>
                            <span style={{ fontSize: '12px', color: emp.isManualEntry ? '#D4A94E' : '#A0A0A0' }}>
                              {emp.isManualEntry ? '📝 Kiosk' : '👤 System'}
                            </span>
                          </td>
                          <td style={styles.td}>
                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                              {/* Step 1: Show "Load PDFs" button if complete and links not yet fetched */}
                              {emp.isComplete && !employeePDFLinks[emp.id] && (
                                <button
                                  onClick={() => handleLoadEmployeePDFs(emp)}
                                  disabled={downloadingEmployeePDFs === emp.id}
                                  style={{ padding: '4px 10px', background: downloadingEmployeePDFs === emp.id ? 'rgba(59,130,246,0.25)' : 'rgba(59,130,246,0.15)', color: '#60A5FA', border: '1px solid rgba(59,130,246,0.3)', borderRadius: '4px', cursor: downloadingEmployeePDFs === emp.id ? 'not-allowed' : 'pointer', fontSize: '12px' }}
                                >
                                  {downloadingEmployeePDFs === emp.id ? '⏳...' : '📄 PDFs'}
                                </button>
                              )}
                              {/* Step 2: Show individual form buttons (each is a direct user click = no popup blocker) */}
                              {employeePDFLinks[emp.id] && employeePDFLinks[emp.id].map(pdf => (
                                <button
                                  key={pdf.submissionId}
                                  onClick={() => window.open(pdf.downloadUrl, '_blank')}
                                  style={{ padding: '4px 8px', background: pdf.formSlug === 'handbook' ? 'rgba(37,99,235,0.15)' : 'rgba(124,58,237,0.15)', color: pdf.formSlug === 'handbook' ? '#60A5FA' : '#A78BFA', border: `1px solid ${pdf.formSlug === 'handbook' ? 'rgba(37,99,235,0.3)' : 'rgba(124,58,237,0.3)'}`, borderRadius: '4px', cursor: 'pointer', fontSize: '11px', whiteSpace: 'nowrap' }}
                                >
                                  {pdf.formSlug === 'handbook' ? '📘 Handbook' : '⚖️ Arbitration'}
                                </button>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
            
            <div style={{ marginTop: '16px', padding: '12px', background: 'rgba(37,99,235,0.1)', borderRadius: '8px', fontSize: '13px', color: '#60A5FA' }}>
              💡 <strong>Tip:</strong> Click "📄 PDFs" to load download links, then click each document individually to open it.
            </div>
            <div style={{ marginTop: '8px', padding: '12px', background: 'rgba(212,169,78,0.1)', borderRadius: '8px', fontSize: '13px', color: '#D4A94E' }}>
              ⚠️ <strong>Warning:</strong> Deleting an employee removes them from compliance tracking and deletes all their document submissions. This action cannot be undone.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HumanResources;
